import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, User, Briefcase, FileText, CreditCard, Tag, Edit2, MessageSquare, Package, Sparkles, Percent } from 'lucide-react';
import CustomDeleteDialog from '../../../components/CustomDeleteDialog';

const BillCard = ({ bill, onEdit, onDelete }) => {
  const { t, i18n } = useTranslation('home');
  const dateLocale = i18n.language === 'en' ? 'en-GB' : 'tr-TR';
  const getDateColor = (dateStr) => {
    const isToday = new Date(dateStr).toDateString() === new Date().toDateString();
    return isToday
      ? 'bg-gradient-to-r from-green-50 to-green-100'
      : 'bg-gradient-to-r from-orange-50 to-orange-100';
  };

  return (
    <div className="w-full max-w-sm bg-white rounded-lg shadow transition-all duration-300 hover:shadow-lg">
      <div className="p-6">
        <div className={`${getDateColor(bill.date)} -mx-6 -mt-6 px-6 py-3 mb-4 rounded-t-lg flex items-center gap-2`}>
          <Calendar className="h-4 w-4 text-gray-600" />
          <span className="font-medium text-gray-700">
            {new Date(bill.date).toLocaleDateString(dateLocale)}
          </span>
        </div>

        <div className="space-y-3 mb-4">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-blue-500" />
            <span className="text-gray-600">
              <span className="font-medium text-gray-900">{t('billCard.customer')}</span> {bill.customerName}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-blue-500" />
            <span className="text-gray-600">
              <span className="font-medium text-gray-900">{t('billCard.employee')}</span> {bill.employeeName}
            </span>
          </div>

          <div className="flex gap-2">
            <div className="flex-shrink-0 mt-1">
              <FileText className="h-4 w-4 text-blue-500" />
            </div>
            <div className="text-gray-600">
              <span className="font-medium text-gray-900">{t('billCard.services')}</span>
              {bill.services.map((service, index) => (
                <span key={`${service.id}-${index}`}>
                  {service.name}
                  {service.sessions > 1 ? ` ${t('billCard.sessions', { count: service.sessions })}` : ''}
                  {index < bill.services.length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          <div className="flex-shrink-0 mt-0.5">
            <MessageSquare className="h-4 w-4 text-blue-500" />
          </div>
          <div className="text-gray-600 text-sm">
            <span className="font-medium text-gray-900">{t('billCard.note')}</span>{' '}
            {bill.note ? (
              <span className="whitespace-pre-wrap">{bill.note}</span>
            ) : (
              <span className="text-gray-400 italic">{t('billCard.noNote')}</span>
            )}
          </div>
        </div>

        {(() => {
          const hasDiscount    = (bill.discount        || 0) > 0;
          const hasPkgDiscount = (bill.packageDiscount || 0) > 0;
          const hasLoyalty     = (bill.loyaltyDiscount || 0) > 0;
          const hasAnyDiscount = hasDiscount || hasPkgDiscount || hasLoyalty;
          return (
            <div className="bg-gray-50 rounded-lg p-3 space-y-2 mb-4">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-blue-500" />
                  <span className="font-medium text-gray-900">{t('billCard.price')}</span>
                </span>
                <div className="flex items-center gap-2">
                  <span className={hasAnyDiscount ? 'line-through text-gray-400 text-sm' : 'text-gray-900 font-medium'}>
                    {bill.price} TL
                  </span>
                  {hasAnyDiscount && (
                    <span className="text-green-600 font-semibold">{bill.totalPrice} TL</span>
                  )}
                </div>
              </div>

              {hasDiscount && (
                <div className="flex items-center justify-between text-orange-600 text-sm">
                  <span className="flex items-center gap-1.5">
                    <Percent className="h-3.5 w-3.5" />
                    {t('billCard.discount')}
                  </span>
                  <span>−{bill.discount} TL</span>
                </div>
              )}

              {hasPkgDiscount && (
                <div className="flex items-center justify-between text-blue-600 text-sm">
                  <span className="flex items-center gap-1.5">
                    <Package className="h-3.5 w-3.5" />
                    Paket indirimi
                  </span>
                  <span>−{bill.packageDiscount} TL</span>
                </div>
              )}

              {hasLoyalty && (
                <div className="flex items-center justify-between text-purple-600 text-sm">
                  <span className="flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" />
                    Sadakat indirimi
                  </span>
                  <span>−{bill.loyaltyDiscount} TL</span>
                </div>
              )}

              <div className="flex items-center justify-between pt-1 border-t border-gray-200">
                <span className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-blue-500" />
                  <span className="font-medium text-gray-900">{t('billCard.payment')}</span>
                </span>
                <span className="text-gray-600">{bill.paymentType}</span>
              </div>
            </div>
          );
        })()}

        <div className="flex gap-2 justify-end">
          <button
            onClick={() => onEdit(bill)}
            className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors duration-300"
          >
            <Edit2 className="h-4 w-4" />
            <span>{t('billCard.edit')}</span>
          </button>
          <CustomDeleteDialog onConfirm={() => onDelete(bill.id)} />
        </div>
      </div>
    </div>
  );
};

export default BillCard;
